import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@osd/ui/card';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { ThemeToggle } from '@osd/ui/theme-toggle';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/theme-switch — live docs for the user-controlled theme switcher.
 *
 * Documents both halves of the contract:
 *   - The `<ThemeToggle>` presentational component (`@osd/ui`).
 *   - The `useTheme()` hook (`@osd/web/src/lib/theme`) that owns
 *     localStorage + `<html data-osd-theme>` + the OS-pref listener.
 *
 * The component is purely visual; the hook is where the state
 * actually lives. That split keeps `@osd/ui` browser-DOM-free.
 */
export const Route = createFileRoute('/ui/_layout/theme-switch')({
  component: ThemeSwitchPage,
});

function ThemeSwitchPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Theme switch"
        hint="user-controlled light / dark palette — sun + moon icons, persisted in localStorage"
      >
        <Stack gap={4}>
          <Text>
            Click the toggle to flip palettes. The chosen state is mirrored to{' '}
            <code>&lt;html data-osd-theme&gt;</code> so the compiled <code>@osd/ui/styles.css</code>{' '}
            tokens can do their thing — see <code>packages/ui/src/styles/_tokens.scss</code> §Dark
            theme override.
          </Text>

          <Card>
            <Stack gap={3}>
              <Text muted>
                Resolved: <strong>light</strong> (toggle shows Moon → go dark)
              </Text>
              <div>
                <ThemeToggle resolved="light" ariaLabel="Switch to dark mode" />
              </div>
            </Stack>
          </Card>

          <Card>
            <Stack gap={3}>
              <Text muted>
                Resolved: <strong>dark</strong> (toggle shows Sun → go light)
              </Text>
              <div>
                <ThemeToggle resolved="dark" ariaLabel="Switch to light mode" />
              </div>
            </Stack>
          </Card>
        </Stack>
      </Section>

      <Section title="Component contract" hint="@osd/ui/theme-toggle — presentational">
        <Stack gap={5}>
          <Text>
            The component takes a <code>resolved</code> value and an <code>onClick</code>. It does
            not subscribe to the theme state itself — that&apos;s the app&apos;s job. Pass whatever
            your hook returns and the glyph + aria-label will track automatically.
          </Text>
          <CodeBlock
            language="tsx"
            title="Minimal usage"
            code={`<ThemeToggle resolved={theme.resolved} onClick={theme.toggle} />`}
          />
          <CodeBlock
            language="tsx"
            title="Custom accessible label"
            code={`<ThemeToggle
  resolved={theme.resolved}
  onClick={theme.toggle}
  ariaLabel="Toggle dark mode"
/>`}
          />
        </Stack>
      </Section>

      <Section title="Hook contract" hint="useTheme() — owns the state">
        <Stack gap={5}>
          <Text>
            The <code>useTheme()</code> hook lives in <code>apps/web/src/lib/theme.ts</code>. It
            returns <code>preference</code> (the user&apos;s stored choice), <code>resolved</code>{' '}
            (the effective palette at this moment), and four stable callbacks: <code>setLight</code>
            , <code>setDark</code>, <code>reset</code> (back to following OS), and{' '}
            <code>toggle</code> (flip from current <code>resolved</code>). The hook also mirrors{' '}
            <code>resolved</code> onto <code>&lt;html data-osd-theme&gt;</code> and subscribes to
            the <code>prefers-color-scheme</code> media query so a user on <code>system</code> sees
            live changes when the OS flips.
          </Text>
          <CodeBlock
            language="tsx"
            title="App shell wiring (excerpt)"
            code={`import { ThemeToggle } from '@osd/ui/theme-toggle';
import { useTheme } from '../../lib/theme';

function HeaderActions() {
  const { resolved, toggle } = useTheme();
  return <ThemeToggle resolved={resolved} onClick={toggle} />;
}`}
          />
          <CodeBlock
            language="tsx"
            title="Three-state preference (light / dark / system)"
            code={`const { preference, setLight, setDark, reset } = useTheme();

<ButtonGroup ariaLabel="Theme preference">
  <Button onClick={setLight}>Light</Button>
  <Button onClick={setDark}>Dark</Button>
  <Button onClick={reset}>System</Button>
</ButtonGroup>`}
          />
        </Stack>
      </Section>

      <Section
        title="How the CSS picks the palette"
        hint="two activation paths, one explicit attribute"
      >
        <Stack gap={3}>
          <Text>
            The dark colour tokens are applied under <strong>two</strong> selectors in{' '}
            <code>packages/ui/src/styles/_tokens.scss</code>:
          </Text>
          <Text>
            <code>:root[data-osd-theme=&quot;dark&quot;]</code> — explicit dark override (the user
            pinned dark). Wins unconditionally.
          </Text>
          <Text>
            <code>
              @media (prefers-color-scheme: dark) {': '}{' '}
              :root:not([data-osd-theme=&quot;light&quot;])
            </code>{' '}
            — OS preference, but only when the user hasn&apos;t pinned light. The
            <code>:not(...)</code> guard means an explicit{' '}
            <code>data-osd-theme=&quot;light&quot;</code> always wins, even on a dark-mode OS.
          </Text>
          <Text>
            For <code>system</code> the attribute is removed entirely, so the media-query block is
            what decides the palette. Pinning <code>light</code> writes{' '}
            <code>data-osd-theme=&quot;light&quot;</code> to gate the media query out.
          </Text>
        </Stack>
      </Section>

      <Section title="Accessibility" hint="glyph signals destination, not current state">
        <Stack gap={3}>
          <Text>
            The icon represents the <strong>destination</strong>, not the current palette — clicking
            the moon (in light mode) takes you to dark, clicking the sun (in dark mode) takes you to
            light. The <code>aria-label</code> tracks the same idea:{' '}
            <em>&quot;Switch to dark mode&quot;</em> vs. <em>&quot;Switch to light mode&quot;</em>.
            Screen-reader users hear what the click will do, not what state they&apos;re already in.
          </Text>
          <Text>
            The button has a square 40×40px tap target — the WCAG-recommended minimum for pointer
            ergonomics — and inherits the shared <code>--osd-focus-ring</code> for keyboard focus.
            Reduced-motion users get the same control with the hover rotate suppressed.
          </Text>
        </Stack>
      </Section>
    </Stack>
  );
}
